import { type RefObject, useEffect, useRef, useState } from 'react';

import { anchorLeft } from '../components/SelectionToolbar';
import { useSelectionStore } from '../stores/selection-store';
import { highlightsSupported, usePendingRangeHighlight } from './usePendingRangeHighlight';

interface PassagePopup {
  /** Offsets into the plan pane's *scrolled content*, so the panel rides along with the passage
   * it points at instead of hanging at a fixed spot while the document scrolls. */
  x: number;
  y: number;
  snippet: string;
}

/** Enough clearance to clear the selected line rather than sit on top of it. */
const POPOVER_GAP = 6;

/** `::highlight()` names the plan pane's CSS styles. */
const STAGED_HIGHLIGHT = 'plan-staged';
const PENDING_HIGHLIGHT = 'plan-pending';

/**
 * "Comment on this" for a passage of the plan — the same gesture as quoting a message or a code
 * span, and deliberately routed to the same place: the composer.
 *
 * Comments used to post one at a time, each starting its own agent turn. That is the wrong shape
 * for reviewing a document: a reader marks three things in one pass, and three separate turns
 * means three separate rewrites, each undoing the last one's context. Staging them in the
 * composer lets the reader mark everything, say one thing about it, and get one revision back.
 */
export function usePlanPassage(containerRef: RefObject<HTMLDivElement | null>, version: number | null) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popup, setPopup] = useState<PassagePopup | null>(null);
  const addPlan = useSelectionStore((s) => s.addPlan);

  /** The range the open popover is about, kept so it can stay painted once the note field takes
   * focus and collapses the real selection. */
  const pendingRange = useRef<Range | null>(null);
  usePendingRangeHighlight(pendingRange, popup, PENDING_HIGHLIGHT);

  const onMouseUp = (e: React.MouseEvent) => {
    // Clicking into the panel — its note field included — also bubbles a mouseup up here.
    // Ignore it, or the panel unmounts out from under the click about to land on it.
    if (popoverRef.current?.contains(e.target as Node)) return;

    const selection = window.getSelection();
    const container = containerRef.current;
    if (!selection || selection.isCollapsed || selection.rangeCount === 0 || !container || version === null) {
      setPopup(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const snippet = selection.toString();
    if (!container.contains(range.commonAncestorContainer) || !snippet.trim()) {
      setPopup(null);
      return;
    }

    // Cloned: the live range collapses along with the selection the moment focus moves.
    pendingRange.current = range.cloneRange();

    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setPopup({
      x: anchorLeft(rect.left - containerRect.left + container.scrollLeft, container.clientWidth),
      y: rect.bottom - containerRect.top + container.scrollTop + POPOVER_GAP,
      snippet,
    });
  };

  const close = () => {
    pendingRange.current = null;
    setPopup(null);
  };

  const attach = (annotate: boolean) => {
    if (!popup || version === null) return;
    addPlan({ version, snippet: popup.snippet }, annotate);
    window.getSelection()?.removeAllRanges();
    close();
  };

  return { onMouseUp, popup, popoverRef, attach, dismiss: close };
}

/**
 * Keep every staged passage visibly marked in the document.
 *
 * Without this, attaching a passage clears the browser selection and the mark you just made
 * vanishes — so on a long plan there is no way to see what you have already commented on except
 * by reading the chips at the bottom and matching them up by eye.
 *
 * Uses the CSS Custom Highlight API, which paints ranges without touching the DOM. That matters
 * here: the alternative is wrapping matched text in `<mark>`, which would mean rewriting a tree
 * react-markdown owns and re-running on every render. Where the API is missing the highlight is
 * simply absent — the chips still list what is staged, so nothing is lost but the shading.
 */
export function useStagedPassageHighlights(
  containerRef: RefObject<HTMLDivElement | null>,
  snippets: string[],
  /** Re-run when the rendered document changes, not just when the staged set does. */
  contentKey: string | null,
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!highlightsSupported()) return;
    if (!container || snippets.length === 0) {
      CSS.highlights.delete(STAGED_HIGHLIGHT);
      return;
    }

    // One flat string of the pane's text, plus the text node each character came from, so a
    // match found in the flat string can be turned back into a DOM range.
    const nodes: Text[] = [];
    const starts: number[] = [];
    let flat = '';
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      nodes.push(node as Text);
      starts.push(flat.length);
      flat += node.textContent ?? '';
    }

    /** The text node containing flat-string offset `at`, and the offset within it. */
    const locate = (at: number): { node: Text; offset: number } | null => {
      // Binary search: a long plan has thousands of text nodes and this runs per match endpoint.
      let lo = 0;
      let hi = nodes.length - 1;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (starts[mid]! <= at) lo = mid;
        else hi = mid - 1;
      }
      const node = nodes[lo];
      if (!node) return null;
      return { node, offset: Math.min(at - starts[lo]!, node.textContent?.length ?? 0) };
    };

    const ranges: Range[] = [];
    for (const snippet of snippets) {
      // Selections cross element boundaries, where `toString()` inserts newlines that the flat
      // text does not have. Matching on the first line alone is what survives that — it is also
      // enough to mark where the passage starts, which is all the highlight is for.
      const needle = snippet.trim().split('\n')[0]?.trim();
      if (!needle || needle.length < 3) continue;

      let from = 0;
      for (;;) {
        const at = flat.indexOf(needle, from);
        if (at === -1) break;
        const start = locate(at);
        const end = locate(at + needle.length);
        if (start && end) {
          const range = document.createRange();
          try {
            range.setStart(start.node, start.offset);
            range.setEnd(end.node, end.offset);
            ranges.push(range);
          } catch {
            // A boundary that moved between the walk and now — skip this one rather than
            // dropping every highlight.
          }
        }
        from = at + needle.length;
      }
    }

    CSS.highlights.set(STAGED_HIGHLIGHT, new Highlight(...ranges));
    return () => {
      CSS.highlights.delete(STAGED_HIGHLIGHT);
    };
  }, [containerRef, snippets, contentKey]);
}
